/*
 * A minimal confirmation dialog used for irreversible actions (cancel a run
 * sweeps orphans + aborts — there is no undo). Backed by Mantine's `Modal` (U6):
 * it renders nothing when closed, traps focus, and closes on Escape / backdrop
 * click (suppressed while a confirm is in flight).
 */

import type { ReactNode } from "react";
import { Button, Group, Modal, Text } from "@mantine/core";

/** Confirm-button intent → Mantine (variant, color). */
type ConfirmVariant = "primary" | "secondary" | "ghost" | "danger";

const CONFIRM_PROPS: Record<ConfirmVariant, { variant: string; color?: string }> = {
  primary: { variant: "filled" },
  secondary: { variant: "default" },
  ghost: { variant: "subtle", color: "gray" },
  danger: { variant: "filled", color: "red" },
};

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  body: ReactNode;
  confirmLabel: string;
  confirmVariant?: ConfirmVariant;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  confirmVariant = "danger",
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirm = CONFIRM_PROPS[confirmVariant];
  return (
    <Modal
      opened={open}
      onClose={() => {
        // Don't let Escape / backdrop dismiss while the action is in flight.
        if (!loading) onCancel();
      }}
      title={title}
      centered
      closeOnEscape={!loading}
      closeOnClickOutside={!loading}
      withCloseButton={false}
      size="sm"
    >
      <Text c="dimmed" fz="sm">
        {body}
      </Text>
      <Group justify="flex-end" gap="sm" mt="lg">
        <Button variant="subtle" color="gray" onClick={onCancel} disabled={loading}>
          Keep running
        </Button>
        <Button
          variant={confirm.variant}
          color={confirm.color}
          onClick={onConfirm}
          loading={loading}
        >
          {confirmLabel}
        </Button>
      </Group>
    </Modal>
  );
}
